import { createFileRoute } from "@tanstack/react-router";
import {
  Container,
  DirectAnswer,
  CtaBand,
  Eyebrow,
  Lead,
  PrimaryLink,
  Quote,
  TextLink,
} from "@/components/site/primitives";
import { SITE, TESTIMONIALS } from "@/lib/site-content";
import { ogImageMeta } from "@/lib/og";
import { RelatedLinks } from "@/components/site/RelatedLinks";
import { Section } from "@/components/site/sections";
import { AREAS, jsonLdScript, serviceSchema } from "@/lib/schema";
import { breadcrumbMeta } from "@/lib/internal-links";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";

const PATH = "/board-advisory" as const;

const title = "Technology NED, board advisory and tech due diligence";
const description =
  "Technology risk your board cannot challenge? Non-executive and advisory board roles for tech businesses and investors: technical risk, delivery, due diligence.";

export const Route = createFileRoute("/board-advisory")({
  head: () => ({
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:url", content: `${SITE.url}/board-advisory` },
      ...ogImageMeta(
        "board-advisory",
        "Board and advisory: technology risk nobody on your board can challenge",
      ),
    ],
    links: [{ rel: "canonical", href: `${SITE.url}/board-advisory` }],
    scripts: [
      breadcrumbMeta(PATH),
      jsonLdScript(
        serviceSchema({
          path: "/board-advisory",
          name: "Board and advisory work | Timothy Ng, Scryla",
          serviceType: "Board advisory and non-executive director",
          areaServed: AREAS.global,
          offerNames: [
            "Non-executive director appointments",
            "Board and founder advisory",
            "Technical due diligence for investors",
          ],
        }),
      ),
    ],
  }),
  component: BoardAdvisoryPage,
});

const BRING = [
  {
    title: "Independent assurance the executive team cannot provide by definition",
    body: "Your CTO should give the board a clear account of the work. Independent assurance is different: someone outside the executive needs to test the roadmap, spend and delivery record without owning the outcome being assessed.",
  },
  {
    title: "Translation in both directions",
    body: "Technical constraint explained to the board in commercial terms, and commercial pressure explained to the engineering team in terms it will act on.",
  },
  {
    title: "Diligence preparation, from the other side of the table",
    body: "I have raised growth capital at series A and B with private equity houses and private funds, so I know what an investor's technical reviewer opens first.",
  },
  {
    title: "A steady hand when something breaks",
    body: "A failed programme, a supplier dispute, a departing technical founder. The value of a standing board relationship is that it is already in place when that happens.",
  },
];

const SHAPES = [
  {
    title: "Non-executive director",
    body: "A formal board seat with the statutory duties that go with it. Suits a company with an established board that has no independent technology voice on it.",
    detail: "Typically a monthly board meeting plus preparation and ad-hoc calls.",
  },
  {
    title: "Advisory board or founder advisor",
    body: "No board seat and no statutory office, so it is quicker to start and easier to end. Suits earlier companies who want the judgement without the governance overhead.",
    detail: "Typically a standing monthly session, with availability in between.",
  },
  {
    title: "Investor-side technical diligence",
    body: "A one-off engagement for an investor or acquirer: what the technology, the team and the technical debt actually amount to, written plainly enough to price.",
    detail: "Scoped to the transaction, delivered as a written report.",
  },
];

const BOARD_QUESTIONS = [
  {
    theme: "Strategy",
    question: "Is the technology plan the one this strategy actually needs?",
    body: "Separating the plan the business needs from the plan the current team is set up to deliver, and saying plainly where those two have drifted apart.",
  },
  {
    theme: "Growth",
    question: "Will this scale, and what does the next stage cost?",
    body: "Reading the architecture, the team shape and the run rate together, so growth assumptions carry a technology cost the board has actually seen.",
  },
  {
    theme: "Turnaround",
    question: "Is the delivery plan credible enough to fund again?",
    body: "An independent read of the delivery record against the commitments, and a judgement on whether the recovery plan is realistic or optimistic.",
  },
  {
    theme: "Performance",
    question: "Why is this team slower than its size suggests?",
    body: "Looking at where the time goes: dependencies, technical debt, unclear ownership, or a roadmap that changes faster than anything can be finished.",
  },
  {
    theme: "Restructure",
    question: "Is the technology function built for the next stage or the last one?",
    body: "Assessing whether the structure, the seniority and the supplier mix still fit, including where the business is dependent on one person or one vendor.",
  },
  {
    theme: "Diligence",
    question: "What will an investor or acquirer expose?",
    body: "Working through what a technical reviewer opens first, so the board finds it before the other side of the table does.",
  },
];

function BoardAdvisoryPage() {
  return (
    <div>
      <section
        aria-labelledby="board-hero-heading"
        className="border-b border-border bg-gradient-to-b from-background to-surface"
      >
        <Container>
          <Breadcrumbs path={PATH} className="mb-6" />
          <Eyebrow>Board and advisory roles</Eyebrow>
          <h1
            id="board-hero-heading"
            className="mt-4 max-w-[22ch] text-[34px] leading-[1.06] font-bold tracking-[-0.035em] md:text-5xl"
          >
            Technology risk nobody on your board can challenge?
          </h1>
          <DirectAnswer className="mt-6">
            I sit on boards and advisory boards of technology-led businesses as the independent
            technology voice: the person who can read the roadmap, the spend and the delivery record
            without reporting to the executive team producing them. Former CTO and COO with twenty
            years in technology leadership, running my own consultancy for over a decade, working
            with boards internationally.
          </DirectAnswer>
          <Lead className="mt-5 max-w-[56ch]">
            Most boards can challenge the numbers. Far fewer can challenge the technology behind
            them, which is where the cost, the risk and the delay usually sit.
          </Lead>

          <RelatedLinks className="mt-5" paths={["/technology-non-executive-director-uk"]} />
          <p className="mt-3 text-[15px]">
            <TextLink to="/case-studies">
              Case studies: engagements I ran, and what changed
            </TextLink>
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <PrimaryLink to="/contact">Arrange a conversation</PrimaryLink>
            <a
              href={SITE.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-lg border border-border-strong bg-background px-6 py-3.5 text-[15px] font-semibold transition-colors hover:border-primary hover:text-primary"
            >
              View my LinkedIn profile
            </a>
          </div>
        </Container>
      </section>

      <Section
        heading="Why boards bring me in"
        headingId="bring-heading"
        tone="surface"
        divider="bottom"
      >
        <ul className="grid gap-5 sm:grid-cols-2">
          {BRING.map((item) => (
            <li key={item.title} className="rounded-lg border border-border bg-card p-6">
              <h3 className="text-lg font-semibold">{item.title}</h3>
              <p className="mt-2 text-[15px] leading-relaxed text-body">{item.body}</p>
            </li>
          ))}
        </ul>
      </Section>

      <Section
        heading="Three ways the relationship is usually set up"
        headingId="shapes-heading"
        lead="Which one fits depends on how formal your board already is, and on whether you want a standing relationship or an answer to a single question."
        divider="bottom"
      >
        <ul className="grid gap-5 md:grid-cols-3">
          {SHAPES.map((item) => (
            <li key={item.title} className="rounded-lg border border-border bg-card p-6">
              <h3 className="text-lg font-semibold">{item.title}</h3>
              <p className="mt-2 text-[15px] leading-relaxed text-body">{item.body}</p>
              <p className="mt-3 text-sm text-muted-foreground">{item.detail}</p>
            </li>
          ))}
        </ul>
        <p className="mt-6 text-[15px]">
          <TextLink to="/non-executive-director-vs-advisor-vs-chair">
            The difference between a NED, an advisor and a chair, in law and in practice
          </TextLink>
        </p>
        <p className="mt-6 max-w-[62ch] text-[15px] leading-relaxed text-body">
          Where that diligence sits inside an actual transaction, on either side of it,{" "}
          <TextLink to="/technology-due-diligence-ma">
            Technology due diligence for M&amp;A
          </TextLink>{" "}
          covers buy-side assessment, sell-side exit readiness, vendor diligence and first-100-day
          planning.
        </p>
        <RelatedLinks className="mt-6" paths={["/technical-due-diligence-investors-uk"]} />
      </Section>

      <Section
        heading="Board questions I have been brought in to answer"
        headingId="board-questions-heading"
        lead="Board work is confidential, so these are the kinds of question rather than named engagements. Each one is the question itself and the judgement it calls for."
        tone="surface"
        divider="bottom"
      >
        <ul className="grid gap-5 sm:grid-cols-2">
          {BOARD_QUESTIONS.map((item) => (
            <li key={item.question} className="rounded-lg border border-border bg-card p-6">
              <p className="text-xs font-semibold tracking-[0.12em] text-primary uppercase">
                {item.theme}
              </p>
              <h3 className="mt-3 text-lg font-semibold">{item.question}</h3>
              <p className="mt-2 text-[15px] leading-relaxed text-body">{item.body}</p>
            </li>
          ))}
        </ul>
      </Section>

      <Section
        heading="Working with the existing CTO"
        headingId="existing-cto-heading"
        divider="bottom"
      >
        <div className="max-w-[62ch] space-y-4 text-base leading-relaxed text-body">
          <p>
            Independent challenge and supporting the CTO are the same job done well. Nothing here is
            a comment on the person in post, and the fastest way to lose a good one is to bring in
            someone who behaves as though it is.
          </p>
          <p>
            In practice that means agreeing up front what I am reading and why, giving the CTO the
            findings before the board sees them, and being useful in between meetings: a second
            opinion on a supplier, a sounding board before a restructure, cover on a project that
            has grown past the time available for it.
          </p>
          <p>
            Where a business wants that support inside the executive line rather than at the board
            table, that is fractional CTO work rather than a non-executive seat.{" "}
            <TextLink to="/what-i-do">See how the two differ in practice</TextLink>.
          </p>
        </div>
      </Section>

      <Section
        eyebrow="AI on the board agenda"
        heading="AI oversight a board can actually test"
        headingId="ai-oversight-heading"
        divider="bottom"
      >
        <p className="max-w-[62ch] text-base leading-relaxed text-body">
          A board does not need the architecture. It needs to know where AI has authority, what it
          is allowed to trust, how uncertainty is surfaced and who is accountable when an answer is
          confidently wrong. I bring those questions to the table in a form the executive can answer
          and the board can verify.
        </p>
        <RelatedLinks className="mt-7" paths={["/ai-governance-for-leadership-teams"]} />
      </Section>

      <Section
        heading="What boards say"
        hiddenHeading
        headingId="board-quote-heading"
        tone="surface"
        divider="bottom"
      >
        <div className="grid gap-5 md:grid-cols-2">
          <Quote item={TESTIMONIALS.steveKellyShort} />
          <Quote item={TESTIMONIALS.richardGregory} />
        </div>
      </Section>

      <CtaBand
        heading="Is there an independent technology voice on your board?"
        body="Send me the shape of the business, who is already round the table, and the question the board keeps failing to get a straight answer to."
        to="/contact"
        cta="Arrange a conversation"
      />
    </div>
  );
}
